import { Copy, Edit, Plus, Send, Trash2 } from 'lucide-react';
import { useState } from 'react';

import type { ContentDeliveryStatus } from '@goenhub/shared';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import {
  useContentDeliveries,
  useDeleteContentDelivery,
  useDuplicateContentDelivery,
  type ContentDeliveryRecord,
} from '@/hooks/useLineData';
import { formatDateTime } from '@/lib/firestoreTime';

import { DeliveryFormDialog } from './DeliveryFormDialog';

function statusBadge(status: ContentDeliveryStatus | undefined) {
  if (!status) return null;
  if (status === 'active') return <Badge variant="approved">配信中</Badge>;
  if (status === 'scheduled') return <Badge variant="review">予定</Badge>;
  return <Badge variant="default">終了</Badge>;
}

function typeLabel(type: string): string {
  return type === 'archive_playback' ? '見逃し配信' : '限定コンテンツ';
}

export function DeliveriesPage() {
  const { user } = useAuth();
  const canEdit = user?.role === 'owner' || user?.role === 'editor';
  const { data, isLoading } = useContentDeliveries();
  const deleteMutation = useDeleteContentDelivery();
  const duplicateMutation = useDuplicateContentDelivery();
  const [editing, setEditing] = useState<ContentDeliveryRecord | null>(null);
  const [creating, setCreating] = useState(false);

  const handleDelete = async (item: ContentDeliveryRecord) => {
    if (!window.confirm(`配信「${item.deliveryCode}」を削除します。よろしいですか？`)) return;
    try {
      await deleteMutation.mutateAsync(item.id);
      toast.success('配信を削除しました');
    } catch (err) {
      toast.error(`削除に失敗しました: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleDuplicate = async (item: ContentDeliveryRecord) => {
    try {
      await duplicateMutation.mutateAsync(item.id);
      toast.success('配信を複製しました（新コードを発番）');
    } catch (err) {
      toast.error(`複製に失敗しました: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <Screen
      title="配信管理"
      description="見逃し配信・限定コンテンツの配信コードを管理します"
      actions={
        canEdit && (
          <Button type="button" variant="primary" onClick={() => setCreating(true)}>
            <Plus />
            新規作成
          </Button>
        )
      }
    >
      {isLoading ? (
        <Spinner />
      ) : data && data.length > 0 ? (
        <div className="rounded bg-[var(--bg-surface)] border border-[var(--border)] overflow-hidden">
          {data.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-4 p-4 border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--bg-hover)] transition-colors"
            >
              <Send className="size-4 text-[var(--text-muted)] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <code className="font-mono font-bold text-[13px] bg-[var(--bg-hover)] px-2 py-0.5 rounded">
                    {item.deliveryCode}
                  </code>
                  {statusBadge(item.status)}
                  <Badge variant="default">{typeLabel(item.contentType)}</Badge>
                </div>
                <div className="text-xs text-[var(--text-muted)] flex items-center gap-3">
                  <span>開始: {formatDateTime(item.startAt)}</span>
                  <span>終了: {formatDateTime(item.endAt)}</span>
                  <span>アクセス: {item.accessCount ?? 0}</span>
                </div>
                {item.previousCodes && item.previousCodes.length > 0 && (
                  <div className="text-xs text-[var(--text-muted)] mt-1">
                    旧コード: {item.previousCodes.join(', ')}
                  </div>
                )}
              </div>
              {canEdit && (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setEditing(item)}
                    title="編集"
                  >
                    <Edit />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDuplicate(item)}
                    disabled={duplicateMutation.isPending}
                    title="複製"
                  >
                    <Copy />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(item)}
                    title="削除"
                  >
                    <Trash2 />
                  </Button>
                </>
              )}
            </div>
          ))}
        </div>
      ) : (
        <EmptyState icon={Send} title="配信が登録されていません" description="「新規作成」から登録できます" />
      )}

      <DeliveryFormDialog
        open={creating || !!editing}
        existing={editing}
        onOpenChange={(o) => {
          if (!o) {
            setCreating(false);
            setEditing(null);
          }
        }}
      />
    </Screen>
  );
}
